import {
  WSButton,
  WSCard,
  WSElement,
  WSLoader,
  WSSectionToolbar,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSFileAccessScope } from "@wingspanhq/files/dist/lib/interfaces";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useDownloadFile } from "../../../query/files/mutations";
import { useMemberPrivateFileQuery } from "../../../query/files/queries";

type Props = {
  workLog?: IWorkLog;
  shimmer?: boolean;
};

type AttachmentData = {
  fileId: string;
};

const AttachmentFilenameCell: React.FC<{ fileId: string }> = ({ fileId }) => {
  const queryFile = useMemberPrivateFileQuery(fileId);

  if (queryFile.isLoading) {
    return <WSLoader.Spinner size="XS" />;
  }

  const filename = queryFile.data?.filename || "Unknown file";
  return <WSTableCell text={filename} />;
};

const AttachmentActionsCell: React.FC<{ fileId: string }> = ({ fileId }) => {
  const [downloadFile, downloadFileMeta] = useDownloadFile();

  const handleView = async () => {
    await downloadFile({
      accessScope: WSFileAccessScope.Private,
      fileId
    });
  };

  return (
    <WSElement>
      <WSButton.Link
        size="S"
        onClick={handleView}
        loading={downloadFileMeta.isLoading}
      >
        View
      </WSButton.Link>
    </WSElement>
  );
};

const getAttachmentsColumns = (): WSTableColumn<AttachmentData>[] => [
  {
    config: {
      header: { text: "" }
    },
    renderFunction: item => <AttachmentFilenameCell fileId={item.data.fileId} />
  },
  {
    config: {
      header: { text: "" },
      justify: "end"
    },
    renderFunction: item => <AttachmentActionsCell fileId={item.data.fileId} />
  }
];

export const WorkLogAdditionalDetailsPayerView: React.FC<Props> = ({
  workLog,
  shimmer
}) => {
  const attachmentIds = workLog?.attachmentIds || [];
  const hasNotes = !!workLog?.notes;
  const hasAttachments = attachmentIds.length > 0;

  const tableData = useMemo(
    () => attachmentIds.map(fileId => ({ id: fileId, data: { fileId } })),
    [attachmentIds]
  );

  const columns = useMemo(() => getAttachmentsColumns(), []);

  if (shimmer) {
    return (
      <WSElement>
        <WSSectionToolbar title="Additional details" />
        <WSCard mt="M" shimmer />
      </WSElement>
    );
  }

  return (
    <WSElement>
      <WSSectionToolbar title="Additional details" />
      <WSCard mt="M">
        {/* Notes Section */}
        <WSElement>
          <WSText.ParagraphSm color="gray500" mb="XS">
            Notes
          </WSText.ParagraphSm>
          <WSText.ParagraphSm color={hasNotes ? "gray700" : "gray400"}>
            {workLog?.notes || "No notes"}
          </WSText.ParagraphSm>
        </WSElement>

        {/* Attachments Section */}
        <WSElement mt="XL">
          <WSText.ParagraphSm color="gray500" mb="S">
            Attachments
          </WSText.ParagraphSm>

          {hasAttachments ? (
            <WSTable<AttachmentData>
              showHeader={false}
              tableData={tableData}
              columns={columns}
            />
          ) : (
            <WSText.ParagraphSm color="gray400">
              No attachments
            </WSText.ParagraphSm>
          )}
        </WSElement>
      </WSCard>
    </WSElement>
  );
};
